import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getMapMarkers } from '../../api/wikiApi';
import styles from './MapPage.module.css';

// Иконки категорий в стиле ГТА
const CATEGORY_ICONS = {
  bank:       { emoji: '🏦', label: 'Банк',                    color: '#ffd700' },
  shop:       { emoji: '🛒', label: 'Магазин',                  color: '#00d4ff' },
  blacksmith: { emoji: '⚒️',  label: 'Кузнец',                  color: '#ff8c00' },
  spawn:      { emoji: '🏠', label: 'Спавн',                    color: '#32cd32' },
  landmark:   { emoji: '🏛️',  label: 'Достопримечательность',   color: '#ff88cc' },
  quest:      { emoji: '❗',  label: 'Квест',                   color: '#ffff00' },
  other:      { emoji: '📍', label: 'Другое',                   color: '#ffffff' },
};

export default function MapPage() {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [activeCategories, setActiveCategories] = useState(new Set(Object.keys(CATEGORY_ICONS)));
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

  const containerRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    getMapMarkers().then((data) => {
      setMarkers(Array.isArray(data) ? data : []);
      setLoading(false);
    });
  }, []);

  const handleImgLoad = () => {
    if (imgRef.current) {
      setImgSize({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
    }
  };

  const toggleCategory = (cat) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
    setSelectedMarker(null);
  };

  // ——— Zoom ———
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((z) => Math.min(4, Math.max(0.3, z + delta)));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ——— Pan ———
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    setSelectedMarker(null);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !dragStart) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  // Touch pan + pinch-to-zoom
  const touchRef = useRef(null);
  const pinchRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      touchRef.current = { x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y };
      pinchRef.current = null;
    } else if (e.touches.length === 2) {
      touchRef.current = null;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = { dist: Math.hypot(dx, dy), zoom };
    }
  }, [pan, zoom]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 1 && touchRef.current) {
      setPan({ x: e.touches[0].clientX - touchRef.current.x, y: e.touches[0].clientY - touchRef.current.y });
    } else if (e.touches.length === 2 && pinchRef.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const scale = dist / pinchRef.current.dist;
      setZoom(Math.min(4, Math.max(0.3, pinchRef.current.zoom * scale)));
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    if (e.touches.length === 0) {
      touchRef.current = null;
      pinchRef.current = null;
    } else if (e.touches.length === 1) {
      // Switched from pinch back to single finger — reset pan origin
      pinchRef.current = null;
      touchRef.current = { x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y };
    }
  }, [pan]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const visibleMarkers = markers.filter((m) => activeCategories.has(m.category || 'other'));

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>🗺️ Карта мира</h1>
        <p className={styles.subtitle}>Интерактивная карта игрового мира</p>
      </div>

      {/* Легенда / фильтры */}
      <div className={styles.legend}>
        {Object.entries(CATEGORY_ICONS).map(([key, { emoji, label, color }]) => (
          <button
            key={key}
            className={`${styles.legendItem} ${activeCategories.has(key) ? styles.legendItemActive : styles.legendItemInactive}`}
            style={{ '--cat-color': color }}
            onClick={() => toggleCategory(key)}
            title={activeCategories.has(key) ? `Скрыть: ${label}` : `Показать: ${label}`}
          >
            <span className={styles.legendEmoji}>{emoji}</span>
            <span className={styles.legendLabel}>{label}</span>
          </button>
        ))}
      </div>

      {/* Карта */}
      <div className={styles.mapWrapper}>
        <div
          ref={containerRef}
          className={`${styles.mapContainer} ${isDragging ? styles.dragging : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div
            className={styles.mapInner}
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
          >
            <img
              ref={imgRef}
              src="/map.png"
              alt="Карта мира"
              className={styles.mapImage}
              onLoad={handleImgLoad}
              draggable={false}
            />

            {/* Метки */}
            {!loading && imgSize.w > 0 && visibleMarkers.map((marker) => {
              const cat = CATEGORY_ICONS[marker.category] || CATEGORY_ICONS.other;
              const isSelected = selectedMarker?.id === marker.id;
              const markerScale = Math.min(1 / zoom, 1);
              return (
                <button
                  key={marker.id}
                  className={`${styles.marker} ${isSelected ? styles.markerSelected : ''}`}
                  style={{
                    left: `${marker.x}%`,
                    top: `${marker.y}%`,
                    '--marker-color': cat.color,
                    '--marker-scale': markerScale,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMarker(isSelected ? null : marker);
                  }}
                  title={marker.title}
                >
                  <span className={styles.markerPin}>
                    <span className={styles.markerEmoji}>{cat.emoji}</span>
                  </span>
                  <span className={styles.markerLabel}>{marker.title}</span>

                  {isSelected && (
                    <div className={styles.markerPopup} onClick={(e) => e.stopPropagation()}>
                      <div className={styles.popupHeader}>
                        <span className={styles.popupEmoji}>{cat.emoji}</span>
                        <span className={styles.popupTitle}>{marker.title}</span>
                      </div>
                      {marker.description && (
                        <p className={styles.popupDesc}>{marker.description}</p>
                      )}
                      <div className={styles.popupCategory} style={{ color: cat.color }}>
                        {cat.label}
                      </div>
                      <button
                        className={styles.popupClose}
                        onClick={() => setSelectedMarker(null)}
                      >✕</button>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Контролы зума */}
        <div className={styles.zoomControls}>
          <button className={styles.zoomBtn} onClick={() => setZoom((z) => Math.min(4, z + 0.2))} title="Приблизить">+</button>
          <button className={styles.zoomBtn} onClick={resetView} title="Сбросить вид">⌂</button>
          <button className={styles.zoomBtn} onClick={() => setZoom((z) => Math.max(0.3, z - 0.2))} title="Отдалить">−</button>
        </div>

        {loading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner} />
            <span>Загрузка меток...</span>
          </div>
        )}

        {!loading && markers.length === 0 && (
          <div className={styles.emptyOverlay}>
            <span>📍</span>
            <p>Метки ещё не добавлены</p>
          </div>
        )}
      </div>

      <p className={styles.hint}>
        🖱️ Колёсико / щипок — масштаб &nbsp;·&nbsp; Зажмите и тяните — перемещение &nbsp;·&nbsp; Нажмите на метку — подробности
      </p>
    </div>
  );
}
